import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Injector,
    Input,
    OnInit,
    Output,
    ViewEncapsulation,
} from '@angular/core';
import { cloneDeep, isDefined, isEqual } from '@fi-shared/utils';
import { BehaviorSubject, merge, ReplaySubject, Subject } from 'rxjs';
import { Animations } from '@fi-shared/utils/animations';
import { ChecklistReportPastChecklistChartSegmentIndex } from '@fi-shared/checklist/fi-checklist2-report/enums/fi-checklist2-report-chart-index-enum';
import { FiChecklist2ReportDetailsViewerBase } from '../../../../shared/fi-checklist2-report-details-viewer-base';
import {
    IChecklist2ReportAnswer,
    IChecklist2ReportDateRange,
    IChecklist2ReportHistoryCheckpointsCount,
    IChecklist2ReportHistoryComplianceData,
    IChecklist2ReportHistoryUnits,
    IChecklistHistoryDateRangeChangeEvent,
    IChecklistV2ReportParameterFilter,
} from '../../../../shared/fi-checklist2-report';
import { ChecklistV2ReportPages } from '@fi-app/shared/checklist/fi-checklist2-report/enums/fi-checklist2-report-pages.enum';
import { FiChecklist2ReportHistoryService } from '@fi-shared/checklist/fi-checklist2-report/services/fi-checklist2-report-history.service';
import { takeUntil } from 'rxjs/operators';
import { Checkpoint2StatusType } from '@fi-shared/checklist/checklist-enum';
import { WidgetTableFilterType } from '@fi-shared/checklist/fi-checklist2-report/enums/fi-checklist2-report-type';
import { FiChecklist2ReportType } from '@fi-shared/checklist/fi-checklist2-report/enums/fi-checklist2-report-type';
import { FiInfoIconMode } from '@fi-shared/info-icon/info-icon.component';
import { FiChecklistReportAvailableBusinessUnitsChecker } from '@fi-shared/checklist/fi-checklist2-report/components/shared/available-business-uints-checker';

@Component({
    selector: 'fi-history-checklist-details',
    templateUrl: './history-checklist-details.component.html',
    styleUrls: ['./history-checklist-details.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-history-checklist-details' },
    animations: [Animations.fade],
    standalone: false,
})
export class FiHistoryChecklistDetailsComponent extends FiChecklist2ReportDetailsViewerBase implements OnInit {
    @Input() currentPastChecklistReportPage: ChecklistV2ReportPages;
    @Output() currentPastChecklistReportPageChange = new EventEmitter<ChecklistV2ReportPages>();
    @Output() checklistHistoryDateRangeChange = new EventEmitter<IChecklistHistoryDateRangeChangeEvent>();

    get checklistHistoryDateRange(): IChecklist2ReportDateRange {
        return this.fiChecklist2ReportDataService.getDateRange(this.reportParameter.Id);
    }

    selectedIterationStatusType: ChecklistReportPastChecklistChartSegmentIndex;

    checklistUnits$ = new BehaviorSubject<IChecklist2ReportHistoryUnits[]>([]);
    filteredChecklistUnits$ = new ReplaySubject<IChecklist2ReportHistoryUnits[]>(1);
    filterChanged$ = new Subject<void>(); // FiHistoryChecklistDetailsUnitPerformanceMetricsComponent should know when filter or period is changed
    isNonComplianceChecklistDataShown$ = new BehaviorSubject<boolean>(false);
    protected availableBusinessUnitsChecker: FiChecklistReportAvailableBusinessUnitsChecker;
    protected availableChecklistScheduleIds$ = new BehaviorSubject<number[]>(undefined);
    protected answersChanged$ = new Subject<void>(); // for reloading gallery (FiChecklistAttachmentsGalleryCard)

    constructor(
        injector: Injector,
        private cdr: ChangeDetectorRef,
        private fiChecklist2ReportHistoryService: FiChecklist2ReportHistoryService,
    ) {
        super(injector);
    }

    ngOnInit() {
        super.ngOnInit();
        this.onFilterChanged();

        // for smart updating available business units on filter change
        this.availableBusinessUnitsChecker = new FiChecklistReportAvailableBusinessUnitsChecker(
            this.injector,
            FiChecklist2ReportType.PastChecklists,
            this.reportData.auditMode,
        );

        this.loadChecklistUnitsData();
        this.fiChecklist2ReportDataService.subscribeOnFilterOrDateRangeChanged(
            this.reportParameter.Id,
            this.ngOnDestroy$,
            () => {
                this.onFilterChanged();
                this.loadChecklistUnitsData();
                this.answersChanged$.next();
            },
        );
        this.setFilteredChecklistUnits();

        this.loadScheduleIds();
        this.fiChecklist2ReportDataService.subscribeOnDateRangeChanged(
            this.reportParameter.Id,
            this.ngOnDestroy$,
            () => {
                this.loadScheduleIds();
            },
        );
    }

    private loadScheduleIds() {
        this.fiChecklist2ReportHistoryService
            .getChecklistScheduleIds(this.checklistHistoryDateRange, this.reportData.auditMode)
            .then((checklistScheduleIds) => {
                this.availableChecklistScheduleIds$.next(checklistScheduleIds);
            });
    }

    private setFilteredChecklistUnits() {
        merge(this.checklistUnits$, this.currentCheckpointStatusTypesFilter$)
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe(() => {
                const historyUnits: IChecklist2ReportHistoryUnits[] = this.filterChecklistUnitsByCheckpointStatusType();
                this.filteredChecklistUnits$.next(historyUnits);

                const isCheckpointsFiltered = this.isCheckpointOrGroupFiltered(
                    this.currentFilter.CheckpointIds,
                    this.availableItems.Checkpoints,
                    this.currentFilter.ChecklistIds,
                );
                const isCheckpointGroupsFiltered = this.isCheckpointOrGroupFiltered(
                    this.currentFilter.CheckpointGroupIds,
                    this.availableItems.CheckpointGroups,
                    this.currentFilter.ChecklistIds,
                );

                this.isNonComplianceChecklistDataShown$.next(
                    historyUnits.some((item: IChecklist2ReportHistoryUnits) => {
                        return (
                            !isCheckpointsFiltered &&
                            !isCheckpointGroupsFiltered &&
                            item.IsComplianceThresholdEnabled &&
                            (item.NonComplianceIterationsCount > 0 || item.ComplianceIterationsCount > 0)
                        );
                    }),
                );
            });
    }

    private isCheckpointOrGroupFiltered(
        selectedIds: number[],
        availableItems: {
            ChecklistId: number;
        }[],
        checklistIds: number[],
    ): boolean {
        if (!selectedIds) return false;
        const filteredItems = this.fiChecklist2ReportFilterService.filterCheckpointsOrGroupsByChecklistIds(
            availableItems,
            checklistIds,
        );
        return selectedIds.length !== filteredItems.length;
    }

    private filterChecklistUnitsByCheckpointStatusType(): IChecklist2ReportHistoryUnits[] {
        const checkpointsStatusTypes: Checkpoint2StatusType[] = this.currentCheckpointStatusTypesFilter$.value;
        const checklistUnits: IChecklist2ReportHistoryUnits[] = this.checklistUnits$.value;

        if (!checkpointsStatusTypes?.length) return checklistUnits;

        return checklistUnits.filter((checklistUnit: IChecklist2ReportHistoryUnits) => {
            return (
                (checkpointsStatusTypes.includes(Checkpoint2StatusType.Ok) && !!checklistUnit.OkCheckpointsCount) ||
                (checkpointsStatusTypes.includes(Checkpoint2StatusType.NotOk) && !!checklistUnit.NotOkCheckpointsCount)
            );
        });
    }

    loadChecklistUnitsData() {
        this.resetDetailedAnswersFilter();
        this.cdr.markForCheck();

        this.fiChecklist2ReportHistoryService
            .getChecklistUnits(this.currentFilter, this.checklistHistoryDateRange, this.reportData.auditMode)
            .then((checklistUnits: IChecklist2ReportHistoryUnits[]) => {
                this.checklistUnits$.next(checklistUnits);
                this.availableBusinessUnitsChecker.setAvailableBusinessUnitIds(
                    checklistUnits.map((unit) => unit.BusinessUnitId),
                    this.currentFilter,
                    this.checklistHistoryDateRange,
                );

                this.updateIsDetailedAnswersReportAvailable();
            });
    }

    onFilterChanged() {
        this.cdr.markForCheck();
        this.filterChanged$.next();
    }

    getPastChecklistUnitsCheckpointsCount(): IChecklist2ReportHistoryCheckpointsCount {
        const checklistUnits: IChecklist2ReportHistoryUnits[] = this.checklistUnits$.value;
        return {
            notFilledCheckpointCount: checklistUnits.reduce(
                (partialSum: number, item) => partialSum + item.NotFilledCheckpointsCount,
                0,
            ),
            notOkCheckpointCount: checklistUnits.reduce(
                (partialSum: number, item) => partialSum + item.NotOkCheckpointsCount,
                0,
            ),
            okCheckpointCount: checklistUnits.reduce(
                (partialSum: number, item) => partialSum + item.OkCheckpointsCount,
                0,
            ),
        };
    }

    getComplianceUnitsData(): IChecklist2ReportHistoryComplianceData {
        const checklistUnits: IChecklist2ReportHistoryUnits[] = this.checklistUnits$.value.filter(
            (checklistUnit: IChecklist2ReportHistoryUnits) => checklistUnit.IsComplianceThresholdEnabled,
        );
        const nonComplianceUnitsCount: number = checklistUnits.reduce(
            (partialSum: number, item: IChecklist2ReportHistoryUnits) => {
                return partialSum + item.NonComplianceIterationsCount;
            },
            0,
        );
        const complianceUnitsCount: number = checklistUnits.reduce(
            (partialSum: number, item: IChecklist2ReportHistoryUnits) => {
                return partialSum + item.ComplianceIterationsCount;
            },
            0,
        );
        const iterationsCount: number = complianceUnitsCount + nonComplianceUnitsCount;
        return {
            nonComplianceUnitsCount,
            iterationsCount,
            complianceUnitsCount,
        };
    }

    isIterationStatusSet(): boolean {
        return isDefined(this.selectedIterationStatusType);
    }

    setCurrentPastChecklistReportPage(page: ChecklistV2ReportPages) {
        this.currentPastChecklistReportPage = page;
        this.currentPastChecklistReportPageChange.emit(this.currentPastChecklistReportPage);
        this.resetDetailedAnswersFilter();
    }

    getStatusProgressData() {
        return this.fiChecklist2ReportHelperService.getStatusProgressDataFromUnits(this.checklistUnits$.value);
    }

    onChartSegmentClick(segmentIndex: ChecklistReportPastChecklistChartSegmentIndex) {
        this.selectedIterationStatusType = segmentIndex;
        this.cdr.detectChanges();
    }

    currentCheckpointStatusTypesFilter$ = new BehaviorSubject<Checkpoint2StatusType[]>([]);

    onDeviationsChartSegmentClick(segmentIndex: ChecklistReportPastChecklistChartSegmentIndex) {
        this.currentCheckpointStatusTypesFilter$.next(this.getCurrentCheckpointStatusTypes(segmentIndex));
        this.resetDetailedAnswersFilter();
    }

    private getCurrentCheckpointStatusTypes(
        segmentIndex: ChecklistReportPastChecklistChartSegmentIndex,
    ): Checkpoint2StatusType[] {
        if (isDefined(segmentIndex))
            return segmentIndex === ChecklistReportPastChecklistChartSegmentIndex.Completed
                ? this.deviationsCheckpointStatusTypes
                : this.nonDeviationsCheckpointStatusTypes;

        return [];
    }

    private readonly deviationsCheckpointStatusTypes: Checkpoint2StatusType[] = [Checkpoint2StatusType.NotOk];
    private readonly nonDeviationsCheckpointStatusTypes: Checkpoint2StatusType[] = [Checkpoint2StatusType.Ok];

    showUnitMetrics(checklistUnit: IChecklist2ReportHistoryUnits) {
        this.currentFilter.BusinessUnitIds = [checklistUnit.BusinessUnitId];
        this.onFilterChanged();
        this.setCurrentPastChecklistReportPage(ChecklistV2ReportPages.ChecklistHistoryUnitMetrics);
        this.loadChecklistUnitsData();
    }

    isPageCurrent(page: ChecklistV2ReportPages): boolean {
        return this.currentPastChecklistReportPage === page;
    }

    //#region detailed answers
    protected isDetailedAnswersReportAvailable: boolean = false;

    detailedAnswerFilter$ = new BehaviorSubject<IChecklistV2ReportParameterFilter>(undefined);

    showDetailedAnswersReport(detailedReportFilter: IChecklistV2ReportParameterFilter) {
        if (this.currentCheckpointStatusTypesFilter$.value.length) {
            detailedReportFilter.CheckpointStatusTypes = this.currentCheckpointStatusTypesFilter$.value;
        }
        this.fiChecklist2ReportHistoryService
            .getChecklistUnits(detailedReportFilter, this.checklistHistoryDateRange, this.reportData.auditMode)
            .then((benchmarkUnits) => {
                this.benchmarkUnits$.next(benchmarkUnits);
                this.updateIsDetailedAnswersReportAvailable();
                this.detailedAnswerFilter$.next(detailedReportFilter);
                this.cdr.markForCheck();
            });
    }

    showDetailedAnswersReportByBusinessUnitId(businessUnitId: number) {
        this.showDetailedAnswersReport(Object.assign({}, this.currentFilter, { BusinessUnitIds: [businessUnitId] }));
    }

    isDetailedReportTableShown(): boolean {
        return isDefined(this.detailedAnswerFilter$.value) && this.isDetailedAnswersReportAvailable;
    }

    benchmarkUnits$ = new BehaviorSubject<IChecklist2ReportHistoryUnits[]>([]);

    resetDetailedAnswersFilter() {
        this.detailedAnswerFilter$.next(undefined);
    }

    private updateIsDetailedAnswersReportAvailable() {
        // detailed answers report is enabled when
        // is only one specific checklist and schedule chosen in the filter
        if (this.isPageCurrent(ChecklistV2ReportPages.ChecklistHistoryUnitMetrics)) {
            this.isDetailedAnswersReportAvailable = true;
            return;
        }
        this.isDetailedAnswersReportAvailable =
            this.currentFilter.ScheduleIds.length === 1 && this.currentFilter.ChecklistIds.length === 1;
    }

    getChecklistName() {
        if (this.currentFilter.ChecklistIds.length === 1) {
            return this.reportData.availableItems.Checklists.find(
                (item) => item.Id === this.currentFilter.ChecklistIds[0],
            ).Name;
        }
    }

    getScheduleName() {
        if (this.currentFilter.ScheduleIds.length === 1) {
            return this.reportData.availableItems.Schedules.find(
                (item) => item.Id === this.currentFilter.ScheduleIds[0],
            ).Name;
        }
    }

    //#endregion

    // hide compliance threshold data in tooltip if more than 1 checklist is selected.
    isComplianceThresholdDataShown(): boolean {
        return this.currentFilter.ChecklistIds?.length === 1;
    }

    protected readonly ChecklistV2ReportPages = ChecklistV2ReportPages;
    protected readonly WidgetTableFilterType = WidgetTableFilterType;
    protected readonly FiChecklist2ReportType = FiChecklist2ReportType;
    protected readonly FiInfoIconMode = FiInfoIconMode;
}
